import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import apiClient from "../apiClient";
import {
  organizationKeys,
  paymentKeys,
  customerRepresentativeKeys,
} from "./queryKeys";
import { receivablesKeys } from "./receivablesQueries";
import type {
  AutoEmailSendingMode,
  Organization,
  ExcelColumnMapping,
  ExcelPreviewResponse,
  OutgoingMailboxStatus,
} from "../../utils/types";
import { toast } from "sonner";
import { orgIdAtom } from "../../store/authAtoms";

type LinkAccountingSoftwareVariables = {
  name: string;
  code?: string;
  realmId?: string;
  state?: string;
  api_url?: string;
  base64_token?: string;
  api_key?: string;
  fncpatnames?: string[];
  client_id?: string;
  client_secret?: string;
  entity_id?: number;
};

type LinkAccountingSoftwareResponse = {
  status: string;
  connected_to?: string;
};

export const useOutgoingMailboxQuery = (
  options?: Omit<
    UseQueryOptions<
      OutgoingMailboxStatus,
      unknown,
      OutgoingMailboxStatus,
      readonly string[]
    >,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: organizationKeys.outgoingMailbox,
    queryFn: async () => {
      const { data } = await apiClient.get<OutgoingMailboxStatus>(
        "/organization/outgoing_mailbox",
      );
      return data;
    },
    ...options,
  });

export const useOrganizationQuery = (
  options?: Omit<
    UseQueryOptions<Organization, unknown, Organization, (string | number)[]>,
    "queryKey" | "queryFn"
  >,
) => {
  const orgId = useAtomValue(orgIdAtom);
  return useQuery({
    queryKey: [...organizationKeys.detail(orgId)],
    queryFn: async () => {
      const { data } = await apiClient.get<Organization>("/organization/");
      return data;
    },
    ...options,
  });
};

export const useTestPriorityConnectionMutation = () => {
  return useMutation({
    mutationFn: async ({
      api_url,
      base64_token,
    }: {
      api_url: string;
      base64_token: string;
    }) => {
      const { data } = await apiClient.post<{
        status: string;
        message: string;
      }>("/organization/accounting_software/test_priority", {
        api_url,
        base64_token,
      });
      return data;
    },
  });
};

export const useLinkAccountingSoftwareMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: LinkAccountingSoftwareVariables) => {
      const { data } = await apiClient.post<LinkAccountingSoftwareResponse>(
        "/organization/accounting_software",
        {
          name: variables.name,
          ...(variables.code && { code: variables.code }),
          ...(variables.realmId && { realm_id: variables.realmId }),
          ...(variables.state && { state: variables.state }),
          ...(variables.api_url && { api_url: variables.api_url }),
          ...(variables.base64_token && {
            base64_token: variables.base64_token,
          }),
          ...(variables.api_key && { api_key: variables.api_key }),
          ...(variables.fncpatnames?.length && {
            fncpatnames: variables.fncpatnames.join(","),
          }),
          ...(variables.client_id && { client_id: variables.client_id }),
          ...(variables.client_secret && {
            client_secret: variables.client_secret,
          }),
          ...(variables.entity_id != null && {
            entity_id: variables.entity_id,
          }),
        },
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

type UpdateAutoEmailSendingModeVariables = {
  auto_email_sending_mode: AutoEmailSendingMode;
};

export const useUpdateAutoEmailSendingModeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateAutoEmailSendingModeVariables) => {
      const { data } = await apiClient.patch<Organization>(
        "/organization/auto_email_sending_mode",
        variables,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

type UpdateDsoSettingsVariables = {
  days_sales: number;
  period: number;
};

export const useUpdateDsoSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateDsoSettingsVariables) => {
      const { data } = await apiClient.patch<Organization>(
        "/organization/dso_settings",
        variables,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      await queryClient.invalidateQueries({ queryKey: ["organization-dso"] });
      await queryClient.invalidateQueries({
        queryKey: ["organization-dso-time-series"],
      });
    },
  });
};

export const useUploadExcelMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      organizationName,
    }: {
      file: File;
      organizationName: string;
    }) => {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await apiClient.post(
        `/organization/upload_excel/${organizationName}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

export const usePreviewExcelUploadMutation = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await apiClient.post<ExcelPreviewResponse>(
        "/organization/upload_excel/preview",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
  });
};

export const useProcessExcelUploadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      columnMappings,
    }: {
      file: File;
      columnMappings: ExcelColumnMapping;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("column_mappings_json", JSON.stringify(columnMappings));

      const { data } = await apiClient.post(
        "/organization/upload_excel/process",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.all,
      });
    },
  });
};

export const useUpdateOrgMfaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mfa_enabled: boolean) => {
      const { data } = await apiClient.patch<Organization>(
        "/organization/mfa",
        { mfa_enabled },
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

export const useUpdateOrgMcpMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mcp_enabled: boolean) => {
      const { data } = await apiClient.patch<Organization>(
        "/organization/mcp",
        { mcp_enabled },
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

type UpdateCollectionEmailVariables = {
  official_outgoing_email?: string | null;
  use_official_outgoing_email?: boolean;
};

export const useUpdateCollectionEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateCollectionEmailVariables) => {
      const { data } = await apiClient.patch<OutgoingMailboxStatus>(
        "/organization/collection_email",
        variables,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: organizationKeys.outgoingMailbox,
      });
    },
  });
};

export type AccountingRefreshStatus = {
  status: "idle" | "in_progress" | "completed" | "failed";
  last_accounting_data_download: string;
  message: string;
};

export type RefreshJobResponse = {
  status: string;
  job_id: string;
  message?: string | null;
};

export const useRefreshInvoicesMutation = () =>
  useMutation({
    mutationFn: async () => {
      const { data } =
        await apiClient.post<RefreshJobResponse>("/organization/refresh_invoices");
      return data;
    },
  });

export const useRefreshCustomersMutation = () =>
  useMutation({
    mutationFn: async () => {
      const { data } =
        await apiClient.post<RefreshJobResponse>("/organization/refresh_customers");
      return data;
    },
  });

export const useRefreshStatusQuery = (
  enabled: boolean = true,
  options?: { pollWhileRefreshing?: boolean },
) =>
  useQuery({
    queryKey: organizationKeys.refreshStatus,
    queryFn: async () => {
      const { data } = await apiClient.get<AccountingRefreshStatus>(
        "/organization/refresh_status",
      );
      return data;
    },
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === "in_progress" || options?.pollWhileRefreshing) {
        return 3000;
      }
      return false;
    },
  });

export const useSetAccountingSoftwareAndRefreshMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post<{
        status: string;
        message: string;
      }>("/organization/accounting_software/set_and_refresh", { name });
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: organizationKeys.refreshStatus,
      });
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    },
  });
};

export const usePriorityRefreshMutation = () =>
  useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{
        status: string;
        message: string;
      }>("/organization/priority_refresh");
      return data;
    },
    onSuccess: async (_data, _vars, context) => {
      // Align with invoice refresh behavior
      const queryClient = context as any;
      if (queryClient?.invalidateQueries) {
        await queryClient.invalidateQueries({
          queryKey: organizationKeys.refreshStatus,
        });
        await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
        await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      }
    },
  });

export const useDefaultLanguageQuery = () => {
  return useQuery({
    queryKey: [...organizationKeys.all, "default-language"],
    queryFn: async () => {
      const { data } = await apiClient.get<{ language: string }>(
        "/organization/default_language",
      );
      return data;
    },
  });
};

export const useUpdateAllCustomersLanguageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (language: string) => {
      const { data } = await apiClient.post<{
        status: string;
        message: string;
        updated_count: number;
      }>("/organization/update_all_customers_language", { language });
      return data;
    },
    onSuccess: async (data) => {
      // Invalidate customer queries to refresh all customer lists
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.all,
      });
      // Also invalidate default language query to refresh the displayed default
      await queryClient.invalidateQueries({
        queryKey: [...organizationKeys.all, "default-language"],
      });
      toast.success("Language Updated", {
        description: data.message,
      });
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      const errorMessage =
        (typeof detail === "object" && detail?.message
          ? detail.message
          : typeof detail === "string"
            ? detail
            : null) ||
        error?.message ||
        "Failed to update customers' language.";
      toast.error("Error", {
        description: errorMessage,
      });
    },
  });
};
